import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { User, Mail, Phone, MapPin, ArrowRight, Tag, X, Check } from 'lucide-react';
import axios from 'axios';
import { resolveImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactInfo = () => {
  const { cart, getCartTotal } = useCart();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    pincode: ''
  });

  const [errors, setErrors] = useState({});
  const [shippingInfo, setShippingInfo] = useState({ shipping_charge: 0, rule_name: '', is_free: false, rule_id: '' });

  // Promo code state
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount_amount, discount_type, discount_value, message }
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');

  // Check for "Buy Now" item in sessionStorage
  const buyNowItem = sessionStorage.getItem('buyNowItem') ? JSON.parse(sessionStorage.getItem('buyNowItem')) : null;
  
  // Determine if this is a "Buy Now" flow or cart checkout flow
  const isBuyNowFlow = !!buyNowItem;
  
  // Get items to display - either the single buyNow item or cart items
  const displayItems = isBuyNowFlow ? [{ ...buyNowItem, id: buyNowItem._id || buyNowItem.id }] : cart;
  
  // Calculate subtotal based on flow type
  const subtotal = isBuyNowFlow 
    ? buyNowItem.price * buyNowItem.quantity 
    : getCartTotal();

  // Fetch shipping info when subtotal changes
  useEffect(() => {
    const fetchShipping = async () => {
      if (subtotal > 0) {
        try {
          const response = await axios.get(`${API}/shipping-rules/calculate?order_total=${subtotal}`);
          setShippingInfo({
            shipping_charge: response.data.shipping_charge,
            rule_name: response.data.rule_name,
            rule_id: response.data.rule_id,
            is_free: response.data.is_free
          });
        } catch (error) {
          console.error('Error fetching shipping:', error);
          setShippingInfo({ shipping_charge: 100, rule_name: 'Standard Shipping', is_free: false, rule_id: '' });
        }
      }
    };
    fetchShipping();
  }, [subtotal]);

  useEffect(() => {
    // Redirect if no items to checkout (neither cart nor buyNow item)
    if (cart.length === 0 && !buyNowItem) {
      navigate('/cart');
    }
  }, [cart, navigate, buyNowItem]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Full name is required';
    }
    
    if (!formData.customer_email.trim()) {
      newErrors.customer_email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.customer_email)) {
      newErrors.customer_email = 'Please enter a valid email';
    }
    
    if (!formData.customer_phone.trim()) {
      newErrors.customer_phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.customer_phone)) {
      newErrors.customer_phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.customer_address.trim()) {
      newErrors.customer_address = 'Delivery address is required';
    }
    
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^[0-9]{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Store contact info, shipping info, and applied promo in sessionStorage for the payment page
      sessionStorage.setItem('checkoutContactInfo', JSON.stringify(formData));
      sessionStorage.setItem('shippingInfo', JSON.stringify(shippingInfo));
      sessionStorage.setItem('isBuyNowFlow', JSON.stringify(isBuyNowFlow));
      if (appliedPromo) {
        sessionStorage.setItem('appliedPromo', JSON.stringify(appliedPromo));
      } else {
        sessionStorage.removeItem('appliedPromo');
      }
      navigate('/payment');
    } else {
      toast({
        title: "Please fix the errors",
        description: "Some fields need your attention.",
        variant: "destructive"
      });
    }
  };

  // Apply promo code via backend validation
  const handleApplyPromo = async () => {
    setPromoError('');
    const code = promoCodeInput.trim();
    if (!code) {
      setPromoError('Please enter a promo code');
      return;
    }
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, { code, subtotal });
      setAppliedPromo(res.data);
      toast({ title: 'Promo applied', description: res.data.message });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not apply promo code';
      setPromoError(detail);
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  // Re-validate applied promo if subtotal changes (e.g. buyNow qty changes)
  useEffect(() => {
    if (!appliedPromo) return;
    // If subtotal now falls below min or discount needs recomputing, re-validate
    axios.post(`${API}/promo/validate`, { code: appliedPromo.code, subtotal })
      .then((res) => setAppliedPromo(res.data))
      .catch(() => {
        setAppliedPromo(null);
        setPromoError('Promo code no longer valid for this order');
      });
  }, [subtotal]);

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCodeInput('');
    setPromoError('');
  };

  if (cart.length === 0 && !buyNowItem) {
    return null;
  }

  const shipping = shippingInfo.shipping_charge;
  const discount = appliedPromo?.discount_amount || 0;
  const total = Math.max(0, subtotal - discount) + shipping;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="contact-info-page">
      <Header />
      <Toaster />

      <div className="bg-black text-white py-6 sm:py-8 md:py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">Checkout</h1>
          <div className="flex items-center mt-4 text-sm">
            <span className="bg-yellow-500 text-black px-3 py-1 rounded-full font-semibold">1. Contact Info</span>
            <div className="w-8 h-0.5 bg-gray-600 mx-2"></div>
            <span className="text-gray-400">2. Payment</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Contact Information Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-4 sm:p-6" data-testid="contact-info-form">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6 flex items-center">
                <User className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-yellow-500" />
                Contact Information
              </h2>
              
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      data-testid="input-customer-name"
                      className={`w-full pl-10 pr-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base ${
                        errors.customer_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.customer_name && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.customer_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      data-testid="input-customer-email"
                      className={`w-full pl-10 pr-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base ${
                        errors.customer_email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  {errors.customer_email && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.customer_email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      data-testid="input-customer-phone"
                      className={`w-full pl-10 pr-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base ${
                        errors.customer_phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="10-digit mobile number"
                    />
                  </div>
                  {errors.customer_phone && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.customer_phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">
                    Delivery Address *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <textarea
                      name="customer_address"
                      value={formData.customer_address}
                      onChange={handleChange}
                      rows="3"
                      data-testid="input-customer-address"
                      className={`w-full pl-10 pr-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base ${
                        errors.customer_address ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter complete delivery address"
                    ></textarea>
                  </div>
                  {errors.customer_address && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.customer_address}</p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">
                    Pincode *
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    data-testid="input-pincode"
                    className={`w-full px-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base ${
                      errors.pincode ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="6-digit pincode"
                  />
                  {errors.pincode && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.pincode}</p>
                  )}
                </div>

                {/* Promo Code Section */}
                <div className="pt-2" data-testid="promo-code-section">
                  <label className="block text-gray-700 font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base flex items-center">
                    <Tag className="w-4 h-4 mr-2 text-yellow-500" />
                    Promo Code
                  </label>
                  {appliedPromo ? (
                    <div
                      className="flex items-center justify-between bg-green-50 border border-green-300 rounded-lg px-3 py-2.5 sm:py-3"
                      data-testid="promo-applied-banner"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-green-800 truncate">
                            {appliedPromo.code} applied
                          </p>
                          <p className="text-xs text-green-700 truncate">
                            You saved ₹{Math.round(appliedPromo.discount_amount).toLocaleString()}
                            {appliedPromo.discount_type === 'percentage' ? ` (${appliedPromo.discount_value}% off)` : ''}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removePromo}
                        data-testid="promo-remove-btn"
                        className="ml-2 flex-shrink-0 text-red-500 hover:bg-red-50 rounded-full p-1.5 transition-colors"
                        aria-label="Remove promo code"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCodeInput}
                        onChange={(e) => { setPromoCodeInput(e.target.value.toUpperCase()); setPromoError(''); }}
                        placeholder="Enter promo code"
                        data-testid="promo-code-input"
                        disabled={promoLoading}
                        className="flex-1 px-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base border-gray-300 uppercase tracking-wide"
                      />
                      <button
                        type="button"
                        onClick={handleApplyPromo}
                        disabled={promoLoading || !promoCodeInput.trim()}
                        data-testid="promo-apply-btn"
                        className="px-5 py-2.5 sm:py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                      >
                        {promoLoading ? '...' : 'Apply'}
                      </button>
                    </div>
                  )}
                  {promoError && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1.5" data-testid="promo-error-msg">{promoError}</p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                data-testid="continue-to-payment-btn"
                className="w-full mt-6 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 sm:py-4 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <span>Continue to Payment</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 sticky top-20 sm:top-24" data-testid="order-summary">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6">Order Summary</h2>
              
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 max-h-64 sm:max-h-96 overflow-y-auto">
                {displayItems.map((item) => (
                  <div key={item.id} className="flex gap-2 sm:gap-4 pb-3 sm:pb-4 border-b">
                    <img
                      src={resolveImageUrl(item.image)}
                      alt={item.name}
                      className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-xs sm:text-sm line-clamp-2">{item.name}</p>
                      <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                      <p className="text-xs sm:text-sm font-bold">₹{(item.price * item.quantity).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold" data-testid="summary-subtotal">₹{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-semibold" data-testid="summary-shipping">
                    {shipping === 0 ? <span className="text-green-600">FREE</span> : `₹${shipping}`}
                  </span>
                </div>
                {appliedPromo && (
                  <div className="flex justify-between text-sm sm:text-base" data-testid="summary-discount-row">
                    <span className="text-green-700 flex items-center gap-1">
                      <Tag className="w-3.5 h-3.5" />
                      Promo ({appliedPromo.code})
                    </span>
                    <span className="font-semibold text-green-700">
                      − ₹{Math.round(appliedPromo.discount_amount).toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 sm:pt-3 flex justify-between items-center">
                  <span className="text-base sm:text-lg font-bold">Total</span>
                  <span className="text-xl sm:text-2xl font-bold text-yellow-600" data-testid="summary-total">₹{total.toLocaleString()}</span>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 text-xs sm:text-sm">
                <p className="font-semibold text-green-800 mb-1 sm:mb-2">✓ Secure Checkout</p>
                <p className="text-green-700">Powered by Razorpay - 100% secure payment</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactInfo;
